import type { AnonymizationClient } from './client'
import type { Analysis, Facts, Overrides, Result, Setup, Target } from './engine'

/** What the pool needs of one engine: `client.ts` in the browser, a stand-in in tests. */
export type EngineClient = Pick<AnonymizationClient, 'broken' | 'configure' | 'analyze' | 'process' | 'viewable' | 'close'>

const IDLE_MS = 60_000
/** The longest an engine may take to start: its runtime, about 20 MB, below 1 Mbit/s; cached, it
 * starts in seconds. One that hangs, for example when the browser cannot instantiate another
 * WebAssembly runtime, then fails and is replaced instead of keeping its files waiting for good. */
export const START_MS = 180_000
const DEFAULT_LIMIT = 4
const LOW_MEMORY_LIMIT = 2
const LOW_MEMORY_GB = 8

/** The promise, or a failure once `ms` have passed without it settling. */
function within<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const late = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('engine_start_timeout')), ms)
  })
  return Promise.race([promise, late]).finally(() => clearTimeout(timer))
}

const failure = (code: string): Result<never> => ({ ok: false, error: { code, field: null, message: '' } })

/** Every engine this computer can run: one per processor core, at most eight. */
export function maxEngines(cores = globalThis.navigator?.hardwareConcurrency): number {
  return Math.min(Math.max(cores || 2, 1), 8)
}

/**
 * The engines started by default: one per processor core but one, so the computer stays usable, and
 * at most four, or two where the browser reports less than 8 GB of memory: each engine is a Pyodide
 * runtime of its own.
 */
export function defaultEngines(
  cores = globalThis.navigator?.hardwareConcurrency,
  memoryGb = (globalThis.navigator as { deviceMemory?: number } | undefined)?.deviceMemory,
): number {
  const limit = memoryGb !== undefined && memoryGb < LOW_MEMORY_GB ? LOW_MEMORY_LIMIT : DEFAULT_LIMIT
  return Math.max(1, Math.min(maxEngines(cores) - 1, limit))
}

interface Waiter {
  resolve: (client: EngineClient) => void
  reject: (error: unknown) => void
}

/**
 * Several anonymization engines, each in a worker of its own, so a batch uses several processor
 * cores (ANON-04). A request goes to an idle engine or waits for one; engines start when work needs
 * them. Each engine holds the token, which never leaves this computer (TOK-02). Engines left idle for
 * a minute are closed, which frees their memory and drops the token from them.
 */
export class EnginePool {
  private readonly idle: EngineClient[] = []
  private readonly waiting: Waiter[] = []
  /** The setup each engine was given; an engine with an older one is closed when it returns. */
  private readonly setups = new WeakMap<EngineClient, Setup | null>()
  /** Engines handed out or starting. */
  private working = 0
  private setup: Setup | null = null
  private idleTimer: ReturnType<typeof setTimeout> | undefined

  constructor(
    private size: number,
    private readonly create: () => EngineClient,
  ) {}

  /** How many engines work at once. */
  get engines(): number {
    return this.size
  }

  /** Changes how many engines work at once; engines beyond it close once their work ends. */
  resize(size: number): void {
    this.size = Math.max(1, size)
    while (this.idle.length > 0 && this.working + this.idle.length > this.size) this.idle.pop()!.close()
    this.serve()
  }

  /** Hands the token and the site's configuration to the engines and returns the token's key ID. */
  async configure(setup: Setup): Promise<string | null> {
    this.setup = setup
    this.closeIdle()
    const client = await this.acquire()
    try {
      return await client.configure(setup)
    } finally {
      this.release(client)
    }
  }

  analyze(source: ArrayBuffer, overrides?: Overrides, present?: string[]): Promise<Result<Analysis>> {
    return this.run((client) => client.analyze(source, overrides, present))
  }

  process(source: ArrayBuffer, overrides?: Overrides, targets?: Target<ArrayBuffer>[]): Promise<Result<{ data: Uint8Array; facts: Facts }>> {
    return this.run((client) => client.process(source, overrides, targets))
  }

  viewable(source: ArrayBuffer): Promise<Result<Uint8Array>> {
    return this.run((client) => client.viewable(source))
  }

  /** Forgets the token and closes the idle engines; the working ones close when they return. */
  close(): void {
    this.setup = null
    this.closeIdle()
  }

  private async run<T>(work: (client: EngineClient) => Promise<Result<T>>): Promise<Result<T>> {
    let client: EngineClient
    try {
      client = await this.acquire()
    } catch {
      return failure('worker_failed') // the engine did not start, e.g. its runtime did not load
    }
    try {
      return await work(client)
    } finally {
      this.release(client)
    }
  }

  private acquire(): Promise<EngineClient> {
    clearTimeout(this.idleTimer)
    let client = this.idle.pop()
    while (client?.broken) {
      client.close() // crashed while idle, for example out of memory
      client = this.idle.pop()
    }
    if (client) {
      this.working++
      return Promise.resolve(client)
    }
    return new Promise((resolve, reject) => {
      this.waiting.push({ resolve, reject })
      this.serve()
    })
  }

  /** Starts engines for waiting requests while there is room. */
  private serve(): void {
    while (this.waiting.length > 0 && this.working + this.idle.length < this.size) {
      const waiter = this.waiting.shift()!
      this.working++
      this.start().then(waiter.resolve, (error: unknown) => {
        this.working--
        waiter.reject(error)
        this.serve()
      })
    }
  }

  /** A new engine with the token. One whose runtime did not load, for example a download cut short,
   * is replaced once by a fresh one: a runtime that failed to start is not used again. */
  private async start(attempts = 2): Promise<EngineClient> {
    const client = this.create()
    const setup = this.setup
    try {
      if (setup !== null) await within(client.configure(setup), START_MS)
    } catch (error) {
      client.close()
      if (attempts > 1) return this.start(attempts - 1)
      throw error
    }
    this.setups.set(client, setup)
    return client
  }

  private release(client: EngineClient): void {
    const surplus = this.working + this.idle.length > this.size
    if (client.broken || surplus || this.setups.get(client) !== this.setup) {
      client.close()
      this.working--
      this.serve()
    } else {
      const waiter = this.waiting.shift()
      if (waiter) return waiter.resolve(client)
      this.working--
      this.idle.push(client)
    }
    if (this.working === 0) {
      clearTimeout(this.idleTimer)
      this.idleTimer = setTimeout(() => this.closeIdle(), IDLE_MS)
    }
  }

  private closeIdle(): void {
    clearTimeout(this.idleTimer)
    for (const client of this.idle.splice(0)) client.close()
  }
}
